import { formatDate, formatISODate } from "@clipboard-health/date-time";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { isCbhApiAxiosError } from "@src/appV2/lib/utils/errorUtils";
import { addHours, addMinutes, isBefore } from "date-fns";
import { fallbackTu } from "@src/appV2/i18n/fallbackTu";

// Mirrors the legacy `RequirementMappingLevel.HCF` value. v2 cannot import from
// the legacy `src/constants/documents` module, but the validate-API contract
// returns this literal string for facility-level requirement documents.
const HCF_REQUIREMENT_LEVEL = "HCF";
// eslint-disable-next-line no-restricted-imports
import { zonedTimeToUtc } from "date-fns-tz";

import {
  INVITE_ERROR_REASONS,
  type InviteErrorReason,
  type InviteValidationParseResult,
  type InviteWarningReason,
  VALIDATE_API_WARNING_REASONS,
  type ValidateApiWarningReason,
} from "../types";

import { type TuFunction } from "@src/appV2/i18n/types";

export const TWO_MINUTES_BUFFER = 2;

/**
 * Max start times for each time slot.
 * These represent the latest possible start time for shifts in each slot.
 */
const TIME_SLOT_MAX_START_HOURS = {
  am: { hours: 11, minutes: 59 },
  pm: { hours: 22, minutes: 59 },
  noc: { hours: 5, minutes: 59 }, // Next day
} as const;

export type TimeSlot = "am" | "pm" | "noc";

export interface NextValidSlot {
  date: Date;
  slot: TimeSlot;
}

export function getMaxStartTimeForSlot(
  timeSlot: TimeSlot,
  startTime: Date,
  timezone: string
): Date {
  const { hours, minutes } = TIME_SLOT_MAX_START_HOURS[timeSlot];
  const dateString = formatISODate(startTime, { timeZone: timezone });

  const hoursString = String(hours).padStart(2, "0");
  const minutesString = String(minutes).padStart(2, "0");

  let maxStartTime = zonedTimeToUtc(`${dateString}T${hoursString}:${minutesString}:00`, timezone);

  // NOC shifts max start time is on the next day
  if (timeSlot === "noc") {
    maxStartTime = addHours(maxStartTime, 24);
  }

  return maxStartTime;
}

export function getNextValidSlot(currentSlot: TimeSlot, currentDate: Date): NextValidSlot {
  const slots: TimeSlot[] = ["am", "pm", "noc"];
  const currentIndex = slots.indexOf(currentSlot);

  if (currentIndex < slots.length - 1) {
    return { date: currentDate, slot: slots[currentIndex + 1] };
  }

  // Move to next day's AM
  return { date: addHours(currentDate, 24), slot: "am" };
}

export function formatNextValidSlot(date: Date, slot: TimeSlot): string {
  // formatDate returns "Jan 15, 2025" format, remove the year
  const formattedDate = formatDate(date);
  const dateWithoutYear = formattedDate.replace(/,?\s*\d{4}$/, "");
  return `${dateWithoutYear} ${slot.toUpperCase()}`;
}

/**
 * True when the slot's max start time has passed in the workplace timezone —
 * i.e. there's no longer any valid start time left in this slot (AM closes at
 * 11:59, PM at 22:59, NOC at 05:59 next day).
 *
 * This is intentionally permissive: an invite is allowed for as long as the
 * slot window is open, even if the shift's own (earlier) start time has already
 * passed — a worker can still pick up the remainder of an AM shift mid-morning.
 *
 * The previous implementation subtracted a 2-hour buffer from the slot max,
 * which wrongly blocked invites for legitimate future shifts (e.g. inviting an
 * 11:30 shift at 10:31 reported "shifts starting in the past"). Dropping the
 * buffer fixes that while keeping the slot-based semantics.
 */
export function isSlotBlocked(timeSlot: TimeSlot, startTime: Date, timezone: string): boolean {
  const now = getServerNow();
  const maxStartTime = getMaxStartTimeForSlot(timeSlot, startTime, timezone);
  return isBefore(maxStartTime, now);
}

const ORDERED_SLOTS: TimeSlot[] = ["am", "pm", "noc"];

/**
 * Picks the first slot today that is still bookable in the workplace timezone.
 * Falls back to tomorrow's AM when all of today's slots have passed their cutoff.
 */
export function getInitialBookableSlot(timezone: string): {
  date: Date;
  slot: TimeSlot;
} {
  const now = getServerNow();
  for (const slot of ORDERED_SLOTS) {
    if (!isSlotBlocked(slot, now, timezone)) {
      return { date: now, slot };
    }
  }
  return { date: addHours(now, 24), slot: "am" };
}

export function getStartTimeError(
  startTime: Date,
  tu: TuFunction = fallbackTu
): string | undefined {
  const now = getServerNow();
  const minStartTime = addMinutes(now, TWO_MINUTES_BUFFER);

  if (isBefore(startTime, minStartTime)) {
    return tu("The start time of an invite must be at least 2 minutes after the current time.");
  }

  return undefined;
}

const KNOWN_ERROR_REASONS = new Set<string>(INVITE_ERROR_REASONS);
const KNOWN_WARNING_REASONS = new Set<string>(VALIDATE_API_WARNING_REASONS);

export interface InviteValidationParseOptions {
  /** When true, `SHIFT_OVERWORKING` is promoted from a soft warning to a hard error */
  restrictOverworkShiftInvites: boolean;
  /**
   * When true, `WORKER_MISSING_REQUIREMENTS` is demoted to a soft warning iff every
   * missing doc is HCF (facility-required). Mirrors legacy stepper behaviour gated
   * on the OVERRIDE_FACILITY_REQUIRED_DOCS_FOR_SHIFT_INVITE workplace flag.
   */
  isFacilityDocumentsOverrideEnabled?: boolean;
}

export function getInviteValidationError(
  error: unknown,
  options: InviteValidationParseOptions
): InviteValidationParseResult {
  if (!isCbhApiAxiosError(error)) {
    return { errorReasons: ["WORKER_CANT_CLAIM_SHIFT"], warningReasons: [] };
  }

  const errorData = error.response.data.errors[0];
  const reasons = errorData?.reasons ?? [];
  const detail = errorData?.detail;

  if (errorData?.code === "spendingControlsExceeded") {
    return {
      errorReasons: ["SPENDING_CONTROLS_EXCEEDED"],
      warningReasons: [],
      ...(detail ? { detail } : {}),
    };
  }

  const errorReasons: InviteErrorReason[] = [];
  const warningReasons: InviteWarningReason[] = [];

  const missingDocuments = extractMissingDocuments(errorData);
  const missingDocumentNames = uniqueNames(missingDocuments);
  const allMissingDocumentsAreHcf =
    missingDocuments.length > 0 &&
    missingDocuments.every((document) => document.level === HCF_REQUIREMENT_LEVEL);

  for (const reason of reasons) {
    if (reason === "WORKER_MISSING_REQUIREMENTS") {
      if (options.isFacilityDocumentsOverrideEnabled && allMissingDocumentsAreHcf) {
        warningReasons.push({
          type: "MISSING_FACILITY_REQUIRED_DOCS",
          details: { missingDocuments: missingDocumentNames },
        });
      } else {
        errorReasons.push("WORKER_MISSING_REQUIREMENTS");
      }
      continue;
    }
    if (KNOWN_ERROR_REASONS.has(reason)) {
      errorReasons.push(reason as InviteErrorReason);
      continue;
    }
    if (KNOWN_WARNING_REASONS.has(reason)) {
      const warningReason = reason as ValidateApiWarningReason;
      if (warningReason === "SHIFT_OVERWORKING" && options.restrictOverworkShiftInvites) {
        errorReasons.push("SHIFT_OVERWORKING");
      } else {
        warningReasons.push({ type: warningReason });
      }
    }
  }

  if (errorReasons.length === 0 && warningReasons.length === 0) {
    return {
      errorReasons: ["WORKER_CANT_CLAIM_SHIFT"],
      warningReasons: [],
      ...(detail ? { detail } : {}),
    };
  }

  return {
    errorReasons,
    warningReasons,
    ...(missingDocumentNames.length > 0 ? { missingDocuments: missingDocumentNames } : {}),
  };
}

interface MissingDocument {
  name: string;
  level?: string;
}

function extractMissingDocuments(
  errorData: Record<string, unknown> | undefined
): MissingDocument[] {
  if (!errorData) {
    return [];
  }

  const rawDocuments = errorData.missingDocuments;
  if (!Array.isArray(rawDocuments)) {
    return [];
  }

  return rawDocuments
    .filter(
      (document): document is { name: string; level?: unknown } =>
        typeof document === "object" && document !== null && typeof document.name === "string"
    )
    .map((document) => ({
      name: document.name.trim(),
      level: typeof document.level === "string" ? document.level : undefined,
    }))
    .filter((document) => document.name.length > 0);
}

function uniqueNames(documents: MissingDocument[]): string[] {
  return Array.from(new Set(documents.map((document) => document.name)));
}
